import * as React from "react";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Cookies from "js-cookie";

import { useSetting } from "@/context";
import { Picker } from "@/components/atoms/Picker";
import { EmptyResultMessage } from "@/components/atoms/EmptyResultMessage";
import Title from "@/components/molecules/Title";
import { AutoCompleteSearch } from "@/components/molecules/AutoCompleteSearch";
import { CommonButton } from "@/components/common/buttons/CommonButton";
import { ChargeSupplyDayList } from "./ChargeSupplyDaysList";
import { ChargeSupplyDaysSum } from "./ChargeSupplyDaysSum";
import { ChargeSupplyDayConstants } from "./ChargeSupplyDaysConstants";
import { Office, ONLY_CONTRACT } from "@/models/office";
import {
  ChargerSupplyDays,
  SearchChargerSupplyDays,
  ChargerSupplyDaysSortKey,
  ChargerSupplyDaysOrderTypes,
  SearchChargerSupplyDaysValidator,
} from "@/models/chargeSupplyDays";
import { getOfficesAPI } from "@/util/api/API";
import {
  getChargerSupplyDaysListAPI,
  getChargerSupplyDaysSummaryAPI,
} from "@/util/api/chargerSupplyDays";
import { SINGLE_OFFICE_ID } from "@/common/CommonConstants";
import { getJSTCurrentTime } from "@/util/datetime";
import { getOfficeOptionLabel } from "@/util";
import { Typography } from "@/components/atoms/Typography";
import { ChargeAmountAnnot } from "@/common/CommonConstants";

function ChargeSupplyDayContent() {
  const setting = useSetting();
  const [offices, setOffices] = React.useState<Office[]>([]);
  const [officeSelected, setOfficeSelected] = React.useState<Office>();
  const [searchData, setSearchData] = React.useState<SearchChargerSupplyDays>({
    targetMonth: new Date(getJSTCurrentTime("date"))
      .toISOString()
      .slice(0, 7)
      .replace("-", ""),
  });
  const [searchDataValidator, setSearchDataValidator] =
    React.useState<SearchChargerSupplyDaysValidator>({});
  const [chargerSupplyDays, setChargerSupplyDays] = React.useState<
    ChargerSupplyDays[]
  >([]);
  const [chargerSupplyDaysSum, setChargerSupplyDaysSum] =
    React.useState<ChargerSupplyDays>();
  const [orderTypes, setOrderTypes] =
    React.useState<ChargerSupplyDaysOrderTypes>({
      targetAt: "desc",
    });
  const [noSerachResult, setNoSearchResult] = React.useState<boolean>(false);
  const [initSearchFlag, setInitSearchFlag] = React.useState<boolean>(false);

  const initSort = React.useCallback(
    (data: ChargerSupplyDays[]) => {
      if (orderTypes.targetAt === "desc") {
        return [...data].sort((a, b) =>
          (b.targetAt || "").localeCompare(a.targetAt || "")
        );
      } else {
        return [...data].sort((a, b) =>
          (a.targetAt || "").localeCompare(b.targetAt || "")
        );
      }
    },
    [orderTypes]
  );

  const getChargerSupplyDays = React.useCallback(
    (search: SearchChargerSupplyDays) => {
      getChargerSupplyDaysListAPI(search)
        .then((res) => {
          setChargerSupplyDays(initSort(res.data));
          setNoSearchResult(!res.data.length ? true : false);
        })
        .catch((e) => {
          console.log("==Error when getChargerSupplyDaysListAPI==", e);
        });

      getChargerSupplyDaysSummaryAPI(search)
        .then((res) => {
          setChargerSupplyDaysSum(res.data);
        })
        .catch((e) => {
          console.log("==Error when getChargerSupplyDaysSummaryAPI==", e);
        });
    },
    [setChargerSupplyDays, setChargerSupplyDaysSum, initSort]
  );

  //Get list office to select
  const getOffices = async () => {
    try {
      const res = await getOfficesAPI(ONLY_CONTRACT);
      setOffices(res.data || []);
      const found = res.data.find(
        (d) => d.id === Number(Cookies.get(SINGLE_OFFICE_ID))
      );
      if (found) {
        setOfficeSelected(found);
        setSearchData({
          ...searchData,
          officeId: found.id,
        });
        setInitSearchFlag(true);
      }
    } catch (err) {
      console.log("==Error when get offices==", err);
    }
  };

  const onChangeOffice = React.useCallback(
    (office: Office | undefined) => {
      setSearchData({
        ...searchData,
        officeId: office?.id,
      });
      setSearchDataValidator({
        ...searchDataValidator,
        officeId: false,
      });
      setOfficeSelected(office);
      Cookies.set(SINGLE_OFFICE_ID, office?.id.toString() || "");
    },
    [searchData, searchDataValidator]
  );

  const onChangeMonth = React.useCallback(
    (month?: string) => {
      setSearchData({
        ...searchData,
        targetMonth: month,
      });
      setSearchDataValidator({
        ...searchDataValidator,
        targetMonth: false,
      });
    },
    [searchData, searchDataValidator]
  );

  const handleSearchData = React.useCallback(() => {
    let haveError = false;
    const searchDataValidatorTemp: SearchChargerSupplyDaysValidator = {};
    if (!officeSelected?.id) {
      haveError = true;
      searchDataValidatorTemp.officeId = true;
    }

    if (!searchData.targetMonth) {
      haveError = true;
      searchDataValidatorTemp.targetMonth = true;
    }

    if (haveError) {
      return setSearchDataValidator({
        ...searchDataValidatorTemp,
      });
    }
    getChargerSupplyDays({ ...searchData });
  }, [searchData, getChargerSupplyDays, officeSelected]);

  // Handle event reset search data
  const onClickResetSearchData = React.useCallback(() => {
    setOfficeSelected(undefined);
    setSearchDataValidator({});
    setSearchData({
      targetMonth: new Date(getJSTCurrentTime("date"))
        .toISOString()
        .slice(0, 7)
        .replace("-", ""),
    });
  }, [setOfficeSelected, setSearchData, setSearchDataValidator]);

  // Handle event change sort type
  const onClickChangeSort = (key: ChargerSupplyDaysSortKey) => {
    const orderTypesTemp: ChargerSupplyDaysOrderTypes = {};
    if (key) {
      orderTypesTemp[key] =
        orderTypes[key] === ("asc" || undefined) ? "desc" : "asc";
      setOrderTypes(orderTypesTemp);
      const chargerSupplyDaysTemp = sortTargetAt(key, orderTypesTemp[key]);
      setChargerSupplyDays(chargerSupplyDaysTemp);
    }
  };

  // Handle event sort charge list
  const sortTargetAt = (
    key: ChargerSupplyDaysSortKey,
    sortType: "desc" | "asc" | undefined
  ) => {
    if (key && sortType) {
      if (key === "targetAt") {
        if (sortType === "asc") {
          return [...chargerSupplyDays].sort((a, b) =>
            (a.targetAt || "").localeCompare(b.targetAt || "")
          );
        } else {
          return [...chargerSupplyDays].sort((a, b) =>
            (b.targetAt || "").localeCompare(a.targetAt || "")
          );
        }
      }
    }
    return [...chargerSupplyDays];
  };

  // First load
  React.useEffect(() => {
    setting.updateTitleHeader(ChargeSupplyDayConstants.SupplyDays);
    getOffices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cookieの営業所IDが営業所リストに存在した場合
  React.useEffect(() => {
    if (initSearchFlag) {
      handleSearchData();
      setInitSearchFlag(false);
    }
  }, [initSearchFlag, handleSearchData]);

  return (
    <React.Fragment>
      <Grid
        container
        spacing={2}
        sx={{ paddingLeft: 2, paddingRight: 2, mt: 3 }}
      >
        <Grid container spacing={2} sx={{ display: "flex", pl: 2 }}>
          <Title>{ChargeSupplyDayConstants.SupplyDays}</Title>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ paddingLeft: 2, paddingRight: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={12} lg={12}>
            <Paper
              sx={{
                p: 2,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={6} md={6} lg={6.1} className="required">
                  <AutoCompleteSearch
                    options={offices}
                    autoHighlight
                    getOptionLabel={getOfficeOptionLabel}
                    value={officeSelected || undefined}
                    onChange={(e, value) => {
                      onChangeOffice(value || undefined);
                    }}
                    label={ChargeSupplyDayConstants.OfficeName}
                    error={searchDataValidator.officeId}
                  />
                </Grid>
                <Grid
                  item
                  xs={2}
                  md={2}
                  lg={2.1}
                  sx={{ pt: "0!important" }}
                  className="required"
                >
                  <Picker
                    type="month"
                    label={ChargeSupplyDayConstants.TargetDate}
                    parse={(input) => `${input.slice(0, 4)}-${input.slice(-2)}`}
                    decode={(out) => out.slice(0, 7).replace("-", "")}
                    value={searchData.targetMonth ?? ""}
                    onChange={onChangeMonth}
                    forSearch
                    error={searchDataValidator.targetMonth}
                  />
                </Grid>
                <Grid
                  item
                  xs={4}
                  md={4}
                  lg={3.8}
                  container
                  justifyContent="flex-end"
                >
                  <CommonButton
                    btnType="Search"
                    onClickBtn={handleSearchData}
                  />
                  <CommonButton
                    btnType="ResetSearchCriteria"
                    onClickBtn={onClickResetSearchData}
                    sx={{ ml: 2 }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          <Grid
            item
            xs={12}
            md={12}
            lg={12}
            container
            justifyContent="flex-end"
          >
            <Typography
              sx={{ fontSize: 16, pb: 0, color: "red", whiteSpace: "pre-wrap" }}
            >
              {ChargeAmountAnnot}
            </Typography>
          </Grid>
          {/* データエリア */}
          <Grid item xs={10} md={10} lg={14}>
            <Paper sx={{ p: 1, display: "flex", flexDirection: "column" }}>
              <ChargeSupplyDaysSum
                chargerSupplyDaysSum={chargerSupplyDaysSum}
              />
            </Paper>
          </Grid>
          <Grid item xs={10} md={10} lg={14}>
            <Paper sx={{ p: 1, display: "flex", flexDirection: "column" }}>
              <ChargeSupplyDayList
                chargerSupplyDays={chargerSupplyDays}
                onClickChangeSort={onClickChangeSort}
                orderTypes={orderTypes}
              />
              {noSerachResult && <EmptyResultMessage />}
            </Paper>
          </Grid>
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export default function ChargeSupplyDay() {
  return <ChargeSupplyDayContent />;
}
